import { KnownSortOrder, ProjectDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo, useState } from 'react';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { UsersListControlsState, useInfiniteQueryUsersList } from '../../users.queries';
import { UserListActions } from '../actions/user-list.actions';

const initialControlsState: UsersListControlsState = {
  sortBy: 'firstName',
  sortOrder: KnownSortOrder.Asc,
  searchTerm: '',
  status: 'active',
  managed: ''
};

const searchParamOptions: UseSearchParamsOptions<UsersListControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

export function useUsersList(projectID: string) {
  const project = useQueryGetProject(projectID);
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);
  const [currentAction, setCurrentAction] = useState<UserListActions | null>(null);
  const setAction = useCallback((action: UserListActions | null) => {
    setCurrentAction(action);
  }, []);
  const projects = useMemo(
    () => [project.data, ...(childProjects.data || [])].filter((it): it is ProjectDto => !!it),
    [project.data, childProjects.data]
  );
  const result = useInfiniteQueryUsersList(projectID, projects, queryData);

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: result.hasNextPage || false,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    onScrollToBottom() {
      result.fetchNextPage();
    }
  };

  const hasChildProjects = useMemo(() => !!(childProjects.data && childProjects.data.length > 1), [childProjects.data]);
  const projectObject = { ...project, hasChildProjects };
  return {
    projectObject,
    result,
    infiniteScroll,
    formData,
    setFormData,
    actions: { currentAction, setAction }
  } as const;
}
